import React, { useMemo, useState } from "react";
import {
    CALL_ANSWER,
    CALL_HANGUP,
    CALL_INVITE,
    CALL_NOTE,
    CALL_REJECT,
    MATRIX_RTC_CALL_NOTIFY,
    MATRIX_RTC_NOTIFICATION,
    ROOM_ENCRYPTED,
    ROOM_MESSAGE,
    ROOM_MEMBER
} from "src/constants/matrixEventTypes";
import styled from "styled-components";
import ChatBubble from "../components/ChatBubble";
import { doesObjectPropertyExist } from "src/utils/helpers";
import { UserKeys } from "src/constants/userDetails";
import { EventStatus } from "matrix-js-sdk/lib/models/event-status";
import StandardModal, { StandardModalActionButton } from "src/components/StandardModal";
import { mxcToHttp } from "../utils";
import { GetReadReceipt } from "../components/children/chatmessages/GetReadReceipt";
import { calculateTimelineMessageDate } from "src/utils/dates";
import VideoCallIcon from "src/img/new/chats/video.png";
import DeclineCallIcon from "src/img/new/chats/decline.png";
import {
    getMeetingEventMetadata,
    getSharedCallHasNotes,
    getSharedCallSummary,
    getSharedCallSummaryUpdate,
    SHARED_CALL_SUMMARY_FIELD,
    WEB_CALL_SUMMARY_FIELD
} from "src/scenes/Chat/utils/meetingCallEventContract";
import { CallCardRow, CallCard
    ,CallCardIcon, CallCardContent, 
    CallCardTitle, CallCardText, 
    CallAgainButton, CallCardActions, 
    CallNotesToggleButton, CallNotesModalList, CallCardNote,
    CallCardNoteHeader, CallCardNoteTime, 
    CallCardNoteBody, CenteredCallNotice,
    CenteredCallNoticeText, CenteredCallNotesButton
} from "../components/useRoomTimeline.styled";

const RoomNotification = styled.div`
    font-size: 12px;
    color: gray;
    width: 100%;
    font-weight: 400;
    text-align: center;
    padding: 3px 0;
`;

const GROUP_CALL_ANNOUNCEMENT_SDP = "group-call-announcement";
const APP_CALL_METADATA_FIELD = "com.memotivationapp.call";
const LEGACY_APP_CALL_METADATA_FIELD = "com.dailyfixme.call";
const APP_TARGET_USER_ID_FIELD = "com.memotivationapp.target_user_id";
const LEGACY_APP_TARGET_USER_ID_FIELD = "com.dailyfixme.target_user_id";
const CALL_NOTE_MESSAGE_FIELD = "com.memotivationapp.call_note";
const CALL_SUMMARY_MESSAGE_FIELD = WEB_CALL_SUMMARY_FIELD;

const getMatrixRtcTerminationType = matrixEvent => {
    if (![MATRIX_RTC_CALL_NOTIFY, MATRIX_RTC_NOTIFICATION].includes(matrixEvent.getType?.())) {
        return "";
    }

    const content = matrixEvent.getContent?.() || {};
    const notificationType = content.notify_type || content.notification_type;
    return ["cancel", "reject"].includes(notificationType) ? notificationType : "";
};

const getCallMetadata = content => {
    return content?.[APP_CALL_METADATA_FIELD] || content?.[LEGACY_APP_CALL_METADATA_FIELD] || {};
};

const getTargetUserIdFromContent = content => {
    return content?.[APP_TARGET_USER_ID_FIELD] || content?.[LEGACY_APP_TARGET_USER_ID_FIELD] || "";
};

/**
 * Custom hook for managing room timeline events. this can still be broken down further for SRP
 * @param {array} roomTimeline 
 * @returns {{
 *      renderTimelineEvent: Function
 *      isAMembershipEvent: Function
 *      isAValidMessageEvent: Function
 *      displayMembershipTimelineEvent: Function
 * }}
 */
export const useRoomTimeline = (matrixClient, roomTimeline, currentChat, currentMatrixUserUuid, ctxUseSendMessage, ctxUseMatrixChatList, ctxUseChatCalls) => {
    const [callNotesModal, setCallNotesModal] = useState(null);

    const downloadCallNotes = notes => {
        const contents = notes.map(note => {
            const time = new Date(note.createdAt).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit"
            });
            return `${note.authorName || getCallUserDisplayName(note.authorUserId)} — ${time}\n${note.body}`;
        }).join("\n\n");
        const url = URL.createObjectURL(new Blob([contents], { type: "text/plain;charset=utf-8" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = "call-notes.txt";
        link.click();
        URL.revokeObjectURL(url);
    };

    const renderCallNotesModal = () => (
        <StandardModal
            open={Boolean(callNotesModal)}
            onClose={() => setCallNotesModal(null)}
            title="Call Notes"
            description="Notes captured by participants during this call."
            footer={
                <StandardModalActionButton
                    type="button"
                    $variant="primary"
                    onClick={() => downloadCallNotes(callNotesModal?.notes || [])}
                >
                    Download notes
                </StandardModalActionButton>
            }
        >
            <CallNotesModalList>
                {renderCallNotes(callNotesModal?.notes || [])}
            </CallNotesModalList>
        </StandardModal>
    );

    const getCallSummary = matrixEvent => {
        const content = matrixEvent.getContent?.() || {};
        const webSummary = content[CALL_SUMMARY_MESSAGE_FIELD];
        if (webSummary) {
            return webSummary;
        }

        const sharedSummary = getSharedCallSummary(content);
        if (!sharedSummary) {
            return null;
        }

        const callId = getMeetingEventMetadata(content)?.callId || content.call_id;
        if (!callId) {
            return null;
        }

        const endedAt = sharedSummary.endedAt || matrixEvent.getTs();
        const participantUserIds = sharedSummary.participantIds;
        return {
            call_id: callId,
            duration_seconds: Math.max(0, Math.round((endedAt - sharedSummary.startedAt) / 1000)),
            participant_user_ids: participantUserIds,
            ended_reason: "hangup",
            ended_by_user_id: matrixEvent.getSender?.() || "",
            outcome: participantUserIds.includes(currentMatrixUserUuid) ? "completed" : "missed",
            is_group_call: participantUserIds.length > 2
        };
    };

    const renderTimelineEvent = (matrixEvent, eventIndex, showAvatar = false, shouldShowTimestamp = null) => {

        if (isAMembershipEvent(matrixEvent)) {
            return (
                <RoomNotification key={matrixEvent.getId()}>{ displayMembershipTimelineEvent(matrixEvent) }</RoomNotification>
            );
        } else if (isACenteredCallNoticeEvent(matrixEvent)) {
            return renderCenteredCallNotice(matrixEvent);
        } else if (isACallSummaryEvent(matrixEvent)) {
            return renderCallSummaryCard(matrixEvent);
        } else if (isAValidMessageEvent(matrixEvent)) {
            return processMessageEvent(matrixEvent, eventIndex, showAvatar, shouldShowTimestamp);
        }

        return null;
    };

    const fetchUserName = (matrixEvent) => {
        const userMatrixID = matrixEvent.getStateKey();
        const user = currentChat?.users.find((user) => user.matrixUserId === userMatrixID);
        return user !== undefined ? `${user.employeeDetails.firstName} ${user.employeeDetails.lastName}` : false;
    };

    const displayMembershipTimelineEvent = (matrixEvent) => {
        const { membership } = matrixEvent.getContent() || {};
        const sender = matrixEvent.getSender();
        const target = matrixEvent.getStateKey();
        const userName = fetchUserName(matrixEvent);
        const isCreator = sender === `@${currentChat.ownerUuid}:memotivationapp.com`;

        if (!userName || !currentChat.isGroupChat) return ""; 
        if (membership === "join") {
            if (sender === target) {
                return isCreator ? `${userName} created the chat.` : `${userName} joined the chat.`;
            } else {
                return `${userName} was added to the chat.`;
            }
        }
        if (membership === "leave") return `${userName} left the chat.`;
        return "";
    };

    const getCallTimelineEvents = (matrixEvent) => {
        const room = matrixClient?.getRoom?.(matrixEvent.getRoomId());
        const liveTimelineEvents = room?.getUnfilteredTimelineSet?.()?.getLiveTimeline?.()?.getEvents?.() || [];
        const combinedEvents = [...(roomTimeline || []), ...liveTimelineEvents];

        return Array.from(new Map(combinedEvents.map(event => [event.getId(), event])).values());
    };

    const getCallRoomHasNotes = matrixEvent => {
        const content = matrixEvent.getContent?.() || {};
        const callRoomId = getMeetingEventMetadata(content)?.matrixRoomId || matrixEvent.getRoomId?.();
        if (!callRoomId) {
            return true;
        }

        const updates = getCallTimelineEvents(matrixEvent)
            .filter(event => {
                const eventContent = event.getContent?.() || {};
                const eventCallRoomId = getMeetingEventMetadata(eventContent)?.matrixRoomId || event.getRoomId?.();
                return eventCallRoomId === callRoomId;
            })
            .map(event => getSharedCallSummaryUpdate(event.getContent?.() || {}))
            .filter(Boolean);

        return getSharedCallHasNotes(updates);
    };

    const getReferenceEventId = event => {
        return event?.getContent?.()?.["m.relates_to"]?.event_id || "";
    };

    const getCallEventsByCallId = (matrixEvent, callId) => {
        if (!callId) {
            return [];
        }

        const referenceEventId = getReferenceEventId(matrixEvent);

        return getCallTimelineEvents(matrixEvent)
            .filter(event => {
                const content = event.getContent?.() || {};
                return content.call_id === callId ||
                    content[CALL_NOTE_MESSAGE_FIELD]?.call_id === callId ||
                    getCallSummary(event)?.call_id === callId ||
                    (referenceEventId && getReferenceEventId(event) === referenceEventId);
            });
    };

    const getGroupCallEventsByCallId = (matrixEvent, groupCallId) => {
        if (!groupCallId) {
            return [];
        }

        const timelineEvents = getCallTimelineEvents(matrixEvent)
            .sort((firstEvent, secondEvent) => firstEvent.getTs() - secondEvent.getTs());
        const currentEventTs = matrixEvent.getTs();
        const groupStartEvent = timelineEvents
            .filter(event => {
                const content = event.getContent?.() || {};

                return event.getType() === CALL_INVITE &&
                    content.call_id === groupCallId &&
                    content?.offer?.sdp === GROUP_CALL_ANNOUNCEMENT_SDP &&
                    !getTargetUserIdFromContent(content) &&
                    event.getTs() <= currentEventTs;
            })
            .pop();

        if (!groupStartEvent) {
            return getCallEventsByCallId(matrixEvent, groupCallId);
        }

        const groupCallIds = new Set([groupCallId]);

        timelineEvents.forEach(event => {
            const content = event.getContent?.() || {};

            if (
                event.getType() === CALL_INVITE &&
                getCallMetadata(content)?.isGroupCall &&
                event.getTs() >= groupStartEvent.getTs() &&
                event.getTs() <= currentEventTs &&
                content.call_id
            ) {
                groupCallIds.add(content.call_id);
            }
        });

        return timelineEvents.filter(event => {
            const content = event.getContent?.() || {};

            return content.call_id &&
                groupCallIds.has(content.call_id) &&
                event.getTs() >= groupStartEvent.getTs() &&
                event.getTs() <= currentEventTs;
        });
    };

    const getGroupCallIdForTimelineEvent = (matrixEvent) => {
        if (!currentChat?.isGroupChat) {
            return "";
        }

        const content = matrixEvent.getContent?.() || {};
        const eventCallId = content.call_id || "";
        const isGroupAnnouncement = matrixEvent.getType() === CALL_INVITE &&
            content?.offer?.sdp === GROUP_CALL_ANNOUNCEMENT_SDP &&
            !getTargetUserIdFromContent(content);

        if (isGroupAnnouncement) {
            return eventCallId;
        }

        const groupCallIdFromMetadata = getCallMetadata(content)?.groupCallId;
        if (groupCallIdFromMetadata) {
            return groupCallIdFromMetadata;
        }

        return getCallTimelineEvents(matrixEvent)
            .filter(event => {
                const eventContent = event.getContent?.() || {};

                return event.getType() === CALL_INVITE &&
                    getCallMetadata(eventContent)?.isGroupCall &&
                    eventContent?.offer?.sdp === GROUP_CALL_ANNOUNCEMENT_SDP &&
                    !getTargetUserIdFromContent(eventContent) &&
                    event.getTs() <= matrixEvent.getTs();
            })
            .sort((firstEvent, secondEvent) => secondEvent.getTs() - firstEvent.getTs())[0]
            ?.getContent?.()
            ?.call_id || "";
    };

    const getCallEventsForTimelineEvent = (matrixEvent, callId) => {
        if (!matrixEvent || !callId) {
            return [];
        }

        const callEvents = getCallEventsByCallId(matrixEvent, callId);

        if (!currentChat?.isGroupChat) {
            return callEvents;
        }

        const content = matrixEvent.getContent?.() || {};
        const isGroupAnnouncement = content?.offer?.sdp === GROUP_CALL_ANNOUNCEMENT_SDP;

        if (getCallMetadata(content)?.isGroupCall || isGroupAnnouncement || matrixEvent.getType() === CALL_HANGUP) {
            const groupCallEvents = getGroupCallEventsByCallId(matrixEvent, callId);

            if (groupCallEvents.length) {
                return groupCallEvents;
            }
        }

        return callEvents;
    };

    const getCallNotesFromEvents = (callEvents) => {
        if (!Array.isArray(callEvents) || callEvents.length === 0) {
            return [];
        }

        return callEvents
            .filter(event => {
                const content = event.getContent?.() || {};
                return (event.getType() === CALL_NOTE && content.note) ||
                    Boolean(content[CALL_NOTE_MESSAGE_FIELD]);
            })
            .map(event => {
                const content = event.getContent?.() || {};
                const note = content[CALL_NOTE_MESSAGE_FIELD] || content.note || {};
                const body = typeof note.body === "string" ? note.body.trim() : "";

                if (!body) {
                    return null;
                }
                const authorUserId = content.author_user_id || event.getSender();

                return {
                    id: note.id || event.getId(),
                    body,
                    authorName: getCallUserDisplayName(authorUserId),
                    authorUserId,
                    createdAt: Number(note.created_at || content.created_at || event.getTs() || Date.now())
                };
            })
            .filter(Boolean)
            .sort((firstNote, secondNote) => firstNote.createdAt - secondNote.createdAt);
    };

    const getMeetingRoomNotes = matrixEvent => {
        const meetingRoomId = getMeetingEventMetadata(matrixEvent.getContent?.() || {})?.matrixRoomId;
        const meetingRoom = meetingRoomId ? matrixClient?.getRoom?.(meetingRoomId) : null;
        const meetingEvents = meetingRoom
            ?.getUnfilteredTimelineSet?.()
            ?.getLiveTimeline?.()
            ?.getEvents?.() || [];

        return meetingEvents
            .filter(event => {
                const effectiveContent = event.getEffectiveEvent?.()?.content || event.getContent?.() || {};
                const content = effectiveContent["m.new_content"] || effectiveContent;
                const originalContent = event.getWireContent?.() || event.getContent?.() || {};

                return event.getType() === ROOM_MESSAGE &&
                    originalContent?.["m.relates_to"]?.rel_type !== "m.replace" &&
                    content.msgtype === "m.text" &&
                    typeof content.body === "string" &&
                    Boolean(content.body.trim());
            })
            .map(event => {
                const effectiveContent = event.getEffectiveEvent?.()?.content || event.getContent();
                const content = effectiveContent["m.new_content"] || effectiveContent;
                const authorUserId = event.getSender() || "";
                const participantName = meetingRoom?.getMember?.(authorUserId)?.name
                    || matrixClient?.getUser?.(authorUserId)?.displayName
                    || "";

                return {
                    id: event.getId() || `${meetingRoomId}-${event.getTs()}`,
                    body: content.body.trim(),
                    authorName: getCallUserDisplayName(authorUserId) || participantName,
                    authorUserId,
                    createdAt: event.getTs()
                };
            })
            .sort((firstNote, secondNote) => firstNote.createdAt - secondNote.createdAt);
    };

    const getCallNotesForSummary = matrixEvent => {
        const summaryTimestamp = matrixEvent.getTs();
        const timelineEvents = getCallTimelineEvents(matrixEvent);
        const previousSummaryTimestamp = timelineEvents
            .filter(event =>
                event.getId() !== matrixEvent.getId() &&
                event.getTs() < summaryTimestamp &&
                Boolean(getCallSummary(event))
            )
            .reduce((latestTimestamp, event) => Math.max(latestTimestamp, event.getTs()), 0);
        const noteEvents = timelineEvents.filter(event => {
            const content = event.getContent?.() || {};
            const isCallNote = event.getType() === CALL_NOTE && Boolean(content.note);
            const isLegacyCallNote = Boolean(content[CALL_NOTE_MESSAGE_FIELD]);

            return event.getTs() > previousSummaryTimestamp &&
                event.getTs() <= summaryTimestamp &&
                (isCallNote || isLegacyCallNote);
        });

        return getCallNotesFromEvents(noteEvents);
    };

    const getUserDisplayNameByMatrixId = (matrixUserId) => {
        const user = currentChat?.users?.find(chatUser => chatUser.matrixUserId === matrixUserId);

        if (!user) {
            return "";
        }

        return `${user.employeeDetails.firstName} ${user.employeeDetails.lastName}`;
    };

    const getCallUserDisplayName = (matrixUserId) => {
        if (matrixUserId === currentMatrixUserUuid) {
            return "You";
        }

        return getUserDisplayNameByMatrixId(matrixUserId) || "Someone";
    };

    const getGroupMemberCallParticipantNames = () => {
        const matrixUserIds = (currentChat?.users || [])
            .map(user => user.matrixUserId)
            .filter(Boolean)
            .sort((firstUserId, secondUserId) => {
                if (firstUserId === currentMatrixUserUuid) {
                    return -1;
                }

                if (secondUserId === currentMatrixUserUuid) {
                    return 1;
                }

                return 0;
            });

        return matrixUserIds.map(getCallUserDisplayName);
    };

    const formatParticipantNames = (participantNames) => {
        const uniqueNames = Array.from(new Set(participantNames.filter(Boolean)));

        if (uniqueNames.length <= 2) {
            return uniqueNames.join(" and ");
        }

        return `${uniqueNames.slice(0, -1).join(", ")}, and ${uniqueNames[uniqueNames.length - 1]}`;
    };

    const getAnsweredGroupCallParticipantNames = (callEvents) => {
        const participantIds = new Set([currentMatrixUserUuid]);

        callEvents.forEach(event => {
            const content = event.getContent?.() || {};
            const targetUserId = getTargetUserIdFromContent(content);
            const isGroupAnnouncement = event.getType() === CALL_INVITE &&
                content?.offer?.sdp === GROUP_CALL_ANNOUNCEMENT_SDP;

            if (isGroupAnnouncement && !targetUserId && event.getSender()) {
                participantIds.add(event.getSender());
                return;
            }

            if (isGroupAnnouncement && targetUserId === event.getSender()) {
                participantIds.add(targetUserId);
                return;
            }

            if (event.getType() !== CALL_ANSWER) {
                return;
            }

            if (event.getSender()) {
                participantIds.add(event.getSender());
            }

            if (targetUserId) {
                participantIds.add(targetUserId);
            }
        });

        const eventParticipantNames = Array.from(participantIds).map(getCallUserDisplayName);
        const groupParticipantNames = getGroupMemberCallParticipantNames();

        if (
            currentChat?.isGroupChat &&
            groupParticipantNames.length > eventParticipantNames.length &&
            groupParticipantNames.length <= 3
        ) {
            return groupParticipantNames;
        }

        return eventParticipantNames;
    };

    const formatCallDuration = (durationMs) => {
        const totalSeconds = Math.max(1, Math.round(durationMs / 1000));
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        const parts = [];

        if (minutes) {
            parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
        }

        if (seconds || parts.length === 0) {
            parts.push(`${seconds} ${seconds === 1 ? "second" : "seconds"}`);
        }

        return parts.join(" ");
    };

    const getCallCardDetails = (matrixEvent) => {
        const summary = getCallSummary(matrixEvent);
        const terminationType = getMatrixRtcTerminationType(matrixEvent);
        const callId = summary?.call_id || matrixEvent.getContent()?.call_id;
        if (!callId) {
            return null;
        }

        const callEvents = getCallEventsForTimelineEvent(matrixEvent, callId);
        const answerEvent = callEvents.find(event => event.getType() === CALL_ANSWER);
        const timestamp = calculateTimelineMessageDate(matrixEvent.getTs())["time"];
        const isMine = summary ? true : matrixEvent.getSender() === currentMatrixUserUuid;
        const isMissed = summary
            ? ["cancelled", "declined", "missed"].includes(summary.outcome)
            : Boolean(terminationType) || matrixEvent.getType() === CALL_REJECT || !answerEvent;
        const endedByUserId = summary?.ended_by_user_id || matrixEvent.getSender?.();
        const isCancelledByOther = (summary?.outcome === "cancelled" || terminationType === "cancel") &&
            endedByUserId !== currentMatrixUserUuid;
        const matchingNotes = getCallNotesFromEvents(callEvents);
        const summaryWindowNotes = getCallNotesForSummary(matrixEvent);
        const meetingRoomNotes = getMeetingRoomNotes(matrixEvent);
        const notes = Array.from(new Map(
            [...matchingNotes, ...summaryWindowNotes, ...meetingRoomNotes].map(note => [note.id, note])
        ).values()).sort((firstNote, secondNote) => firstNote.createdAt - secondNote.createdAt);

        return {
            isMine,
            isMissed,
            isCancelledByOther,
            hasNotes: getCallRoomHasNotes(matrixEvent),
            notes,
            summary,
            terminationType,
            timestamp
        };
    };

    const renderCallSummaryCard = (matrixEvent) => {
        const cardDetails = getCallCardDetails(matrixEvent);
        if (!cardDetails) {
            return null;
        }

        const icon = cardDetails.isMissed ? DeclineCallIcon : VideoCallIcon;
        const title = cardDetails.summary
            ? formatMatrixRtcCallSummary(cardDetails.summary)
            : cardDetails.terminationType
                ? formatMatrixRtcTermination(matrixEvent, cardDetails.terminationType)
                : displayCallTimelineEvent(matrixEvent);
        const shouldShowCallAgain = ctxUseChatCalls?.openCallLobby && !cardDetails.isCancelledByOther;
        const hasNotes = cardDetails.hasNotes && cardDetails.notes.length > 0;
        const showNotes = () => setCallNotesModal({ eventId: matrixEvent.getId(), notes: cardDetails.notes });

        return (
            <CallCardRow key={matrixEvent.getId()} $isMine={cardDetails.isMine}>
                <CallCard
                    $cancelled={cardDetails.isCancelledByOther}
                    $isMine={cardDetails.isMine}
                    $missed={cardDetails.isMissed}
                >
                    <CallCardIcon src={icon} alt="" />
                    <CallCardContent>
                        <CallCardTitle
                            $cancelled={cardDetails.isCancelledByOther}
                            $missed={cardDetails.isMissed}
                        >
                            {title}
                        </CallCardTitle>
                        <CallCardText>{cardDetails.timestamp}</CallCardText>
                    </CallCardContent>
                    {(hasNotes || shouldShowCallAgain) &&
                        <CallCardActions>
                            {shouldShowCallAgain &&
                                <CallAgainButton
                                    type="button"
                                    onClick={() => ctxUseChatCalls.openCallLobby(currentChat)}
                                >
                                    Call again
                                </CallAgainButton>
                            }
                            {hasNotes &&
                                <CallNotesToggleButton type="button" onClick={showNotes}>
                                    View Notes
                                </CallNotesToggleButton>
                            }
                        </CallCardActions>
                    }
                </CallCard>
                {callNotesModal?.eventId === matrixEvent.getId() && renderCallNotesModal()}
            </CallCardRow>
        );
    };

    const renderCallNotes = (notes) => {
        return notes.map(note => (
            <CallCardNote key={note.id}>
                <CallCardNoteHeader>
                    <span>{note.authorName || getCallUserDisplayName(note.authorUserId)}</span>
                    <CallCardNoteTime>
                        {calculateTimelineMessageDate(note.createdAt)["time"]}
                    </CallCardNoteTime>
                </CallCardNoteHeader>
                <CallCardNoteBody>{note.body}</CallCardNoteBody>
            </CallCardNote>
        ));
    };

    const renderCenteredCallNotice = (matrixEvent) => {
        const groupCallId = getGroupCallIdForTimelineEvent(matrixEvent);
        const callEvents = groupCallId
            ? getGroupCallEventsByCallId(matrixEvent, groupCallId)
            : getCallEventsForTimelineEvent(matrixEvent, matrixEvent.getContent()?.call_id);
        const notes = getCallNotesFromEvents(callEvents);
        const hasNotes = notes.length > 0;
        const showNotes = () => setCallNotesModal({ eventId: matrixEvent.getId(), notes });

        return (
            <CenteredCallNotice key={matrixEvent.getId()}>
                <CenteredCallNoticeText>
                    {displayCallTimelineEvent(matrixEvent)}
                    {hasNotes &&
                        <>
                            {" "}
                            <CenteredCallNotesButton type="button" onClick={showNotes}>
                                notes
                            </CenteredCallNotesButton>
                        </>
                    }
                </CenteredCallNoticeText>
                {callNotesModal?.eventId === matrixEvent.getId() && renderCallNotesModal()}
            </CenteredCallNotice>
        );
    };

    const displayCallTimelineEvent = (matrixEvent) => {
        const callId = matrixEvent.getContent()?.call_id;
        if (!callId) {
            return "";
        }

        const content = matrixEvent.getContent();
        const targetUserId = getTargetUserIdFromContent(content);
        const isGroupAnnouncement = content?.offer?.sdp === GROUP_CALL_ANNOUNCEMENT_SDP;
        const isGroupCallEvent = currentChat?.isGroupChat && getCallMetadata(content)?.isGroupCall;
        const senderName = getCallUserDisplayName(matrixEvent.getSender());

        if (isGroupCallEvent && matrixEvent.getType() === CALL_INVITE) {
            if (targetUserId && isGroupAnnouncement && targetUserId === matrixEvent.getSender()) {
                return `${senderName} joined the call.`;
            }

            if (!targetUserId && isGroupAnnouncement) {
                return "";
            }

            return "";
        }

        const callEvents = getCallEventsForTimelineEvent(matrixEvent, callId);
        const answerEvent = callEvents.find(event => event.getType() === CALL_ANSWER);

        if (
            currentChat?.isGroupChat &&
            matrixEvent.getType() === CALL_HANGUP &&
            !answerEvent
        ) {
            return "";
        }

        if (matrixEvent.getType() === CALL_REJECT) {
            if (matrixEvent.getSender() === currentMatrixUserUuid) {
                return "You declined the call.";
            }

            return senderName
                ? `${senderName} declined the call.`
                : "The call was declined.";
        }

        if (!answerEvent) {
            if (matrixEvent.getSender() === currentMatrixUserUuid) {
                return "You cancelled the call.";
            }

            return "The call was not answered.";
        }

        const duration = formatCallDuration(matrixEvent.getTs() - answerEvent.getTs());

        if (currentChat?.isGroupChat) {
            const participantNames = formatParticipantNames(getAnsweredGroupCallParticipantNames(callEvents));

            return `${participantNames} were on a call for ${duration}.`;
        }

        return `You had a call for ${duration}.`;
    };

    const formatMatrixRtcCallSummary = summary => {
        const endedByName = getCallUserDisplayName(summary?.ended_by_user_id);
        if (summary?.outcome === "declined") {
            return endedByName === "You"
                ? "You declined the call."
                : `${endedByName || "The recipient"} declined the call.`;
        }
        if (summary?.outcome === "cancelled") {
            return endedByName === "You"
                ? "You cancelled the call."
                : "The call was cancelled.";
        }
        if (summary?.outcome === "missed") {
            return endedByName && endedByName !== "You"
                ? `You missed a call from ${endedByName}.`
                : "You missed a call.";
        }

        const participantNames = (summary?.participant_user_ids || [])
            .map(getCallUserDisplayName)
            .filter(Boolean);
        const names = formatParticipantNames(participantNames.length ? participantNames : ["You"]);
        const duration = formatCallDuration(Number(summary?.duration_seconds || 1) * 1000);

        return participantNames.length > 1
            ? `${names} were on a call for ${duration}.`
            : `You had a call for ${duration}.`;
    };

    const formatMatrixRtcTermination = (matrixEvent, terminationType) => {
        const senderName = getCallUserDisplayName(matrixEvent.getSender?.());
        if (terminationType === "reject") {
            return senderName === "You"
                ? "You declined the call."
                : `${senderName || "The recipient"} declined the call.`;
        }

        return senderName === "You"
            ? "You cancelled the call."
            : `${senderName || "The caller"} cancelled the call.`;
    };

    const getReplyEvent = (matrixEvent) => {
        const roomId = matrixEvent.getRoomId(); 
        const replyEventId = matrixEvent.replyEventId;

        const room = matrixClient.getRoom(roomId);
        const foundEvent = room?.findEventById(replyEventId);
        return foundEvent;
    };

    const { replacementEventsMap } = useMemo(() => {
        if (!roomTimeline || !Array.isArray(roomTimeline)) {
            return { replacementEventsMap: new Map() };
        }

        const replacementMap = new Map();

        roomTimeline.forEach(event => {
            const content = event.getContent();
            const relatesTo = content?.["m.relates_to"];
            if (relatesTo?.rel_type === "m.replace" && relatesTo?.event_id) {
                replacementMap.set(relatesTo.event_id, event);
            }
        });

        return { replacementEventsMap: replacementMap };
    }, [roomTimeline]);

    /** 
     * @typedef {import('matrix-js-sdk').MatrixEvent} MatrixEvent
     * @param {MatrixEvent} matrixEvent 
     * @returns {React.ReactElement|string|undefined}
     */
    const processMessageEvent = (matrixEvent, eventIndex, showAvatar = false, shouldShowTimestamp = null) => {

        const replacementEvent = replacementEventsMap.get(matrixEvent.getId());
      
        const isEdited = !!replacementEvent;

        if (replacementEvent) {
            const replacementContent = replacementEvent.getContent()?.["m.new_content"];
            if (replacementContent) {
                const originalGetContent = matrixEvent.getContent.bind(matrixEvent);
                matrixEvent.getContent = () => ({
                    ...replacementContent,
                    "m.old_message_body": originalGetContent()?.body,
                });
            }
        }

        const { hasBeenRead, usersProfile } = GetReadReceipt(matrixEvent, matrixClient, ctxUseMatrixChatList);
        const senderDetails = currentChat.users.find(user => user.matrixUserId === matrixEvent.getSender());
        if (!senderDetails) {
            console.warn("Sender details not found: ", senderDetails, matrixEvent, eventIndex);
            return "";
        }
        
        if (!matrixEvent.getClient) {
            matrixEvent.getClient = () => matrixClient;
        }
        
        if (matrixEvent.isRedacted()) { 
            return "";
        }

        // Edit
        const content = matrixEvent.getContent();
        
        const replyEvent = getReplyEvent(matrixEvent);
        const replyMessage = replyEvent?.getContent().body;
        const replyUserDetails = replyEvent ? currentChat.users.find(user => user.matrixUserId === replyEvent.getSender()) : null;
        const replyUserFullName = replyUserDetails ? replyUserDetails.employeeDetails.firstName + " " + replyUserDetails.employeeDetails.lastName : "";
        
        /**
         * see https://spec.matrix.org/v1.8/client-server-api/#local-echo for definition
         * used to determine whether we display the timestamp or a sending status */
        const isLocalEcho = [EventStatus.SENDING, EventStatus.ENCRYPTING, EventStatus.QUEUED].includes(matrixEvent.status);
        const isFailedLocalEcho = matrixEvent.status === EventStatus.NOT_SENT;
        if (isFailedLocalEcho) return null;

        const message = matrixEvent.getContent().body;
        const isEncryptedMessageWithoutContent =
            matrixEvent.getType() === ROOM_ENCRYPTED &&
            matrixEvent.isDecrypted?.() !== true;

        if ((!message || message.trim() === "") && !isEncryptedMessageWithoutContent) return null;
        const isChatByCurrentUser = matrixEvent.getSender() === currentMatrixUserUuid;

        const previousSenderUserId = findPreviousSenderUserId(eventIndex);
        const nextSenderUserId = findNextSenderUserId(eventIndex);

        /**
         * Show timestamp only if previous message of the same user is more than 5 minutes or if it is the last message
         * in the thread sent by the same user (treated as the first of consecutive in the code)
         */
        const isFirstOfConsecutive = matrixEvent.getSender() !== previousSenderUserId;
        const isLastOfConsecutive = matrixEvent.getSender() !== nextSenderUserId;

        // download url
        let rawUrl = content.url;
        if (rawUrl && typeof rawUrl === "object" && rawUrl.content_uri) {
            rawUrl = rawUrl.content_uri;
        }
        const url = rawUrl && typeof rawUrl === "string" ? mxcToHttp(rawUrl, matrixClient.baseUrl) : null;

        if (currentChat.matrixRoomId === matrixEvent.getRoomId()) {
            return (
                <div key={matrixEvent.getId()} data-custom-attribute={matrixEvent.getId()}>
                    <ChatBubble
                        matrixClient={matrixClient}
                        matrixEvent={matrixEvent}
                        key={matrixEvent.getId()}
                        message={message}
                        timestamp={matrixEvent.getTs()}
                        isChatByCurrentUser={isChatByCurrentUser}
                        photo={senderDetails.employeeDetails[UserKeys.PROFILE_PICTURE_URL]}
                        firstName={senderDetails.employeeDetails[UserKeys.FIRST_NAME]}
                        lastName={senderDetails.employeeDetails[UserKeys.LAST_NAME]}
                        isChatReplyByCurrentUser={replyEvent?.getSender() === currentMatrixUserUuid}
                        replyEvent={replyEvent}
                        currentMatrixUserUuid={currentMatrixUserUuid}
                        replyMessage={replyMessage}
                        replyUserFullName={replyUserFullName}
                        isFirstOfConsecutive={isFirstOfConsecutive}
                        isLastOfConsecutive={isLastOfConsecutive}
                        isSending={isLocalEcho}
                        showTimeStamp={shouldShowTimestamp }
                        isEdited={isEdited}
                        downloadUrl={url}
                        isFile={ctxUseSendMessage?.queued?.isFile}
                        hasBeenRead={hasBeenRead}
                        usersProfile={usersProfile}
                        senderDetails={senderDetails}
                        showAvatar={showAvatar}
                    />
                </div>
            );
        }
    };

    /**
     * also excludes message events with signs of redaction
     * event messages when redacted, does not change event type. a `redacted_because` property gets added instead
     * @param {object} event 
     * @returns boolean */
    const isAValidMessageEvent = (matrixEvent) => {
        const content = matrixEvent.getContent?.() || {};
        return [ROOM_ENCRYPTED, ROOM_MESSAGE].includes(matrixEvent.getType()) &&
            !content[CALL_NOTE_MESSAGE_FIELD] &&
            !content[CALL_SUMMARY_MESSAGE_FIELD] &&
            !content[SHARED_CALL_SUMMARY_FIELD] &&
            !doesObjectPropertyExist(matrixEvent, "redacted_because");
    };
    
    const isAMembershipEvent = (matrixEvent) => {
        return matrixEvent.getType() === ROOM_MEMBER;
    };

    const isACenteredCallNoticeEvent = (matrixEvent) => {
        if (!currentChat?.isGroupChat || matrixEvent.getType() !== CALL_HANGUP) {
            return false;
        }

        const callEvents = getCallEventsForTimelineEvent(matrixEvent, matrixEvent.getContent()?.call_id);
        const answerEvent = callEvents.find(event => event.getType() === CALL_ANSWER);

        return Boolean(answerEvent) &&
            !hasEarlierAnsweredGroupHangupInBatch(matrixEvent);
    };

    const hasEarlierAnsweredGroupHangupInBatch = (matrixEvent) => {
        return getCallTimelineEvents(matrixEvent).some(event => {
            if (
                event.getId() === matrixEvent.getId() ||
                event.getType() !== CALL_HANGUP ||
                event.getTs() >= matrixEvent.getTs() ||
                matrixEvent.getTs() - event.getTs() > 5000
            ) {
                return false;
            }

            return getCallEventsForTimelineEvent(event, event.getContent()?.call_id)
                .some(callEvent => callEvent.getType() === CALL_ANSWER);
        });
    };

    const isACallSummaryEvent = (matrixEvent) => {
        const summary = getCallSummary(matrixEvent);
        if (summary) {
            const callId = summary.call_id;
            const canonicalSummary = getCallTimelineEvents(matrixEvent)
                .filter(event => getCallSummary(event)?.call_id === callId)
                .sort((firstEvent, secondEvent) => {
                    const firstSharedSummary = getSharedCallSummary(firstEvent.getContent?.() || {});
                    const secondSharedSummary = getSharedCallSummary(secondEvent.getContent?.() || {});
                    const completionDifference = Number(Boolean(secondSharedSummary?.endedAt)) -
                        Number(Boolean(firstSharedSummary?.endedAt));
                    if (completionDifference) {
                        return completionDifference;
                    }
                    const participantDifference = (secondSharedSummary?.participantIds.length || 0) -
                        (firstSharedSummary?.participantIds.length || 0);
                    if (participantDifference) {
                        return participantDifference;
                    }
                    const timestampDifference = secondEvent.getTs() - firstEvent.getTs();
                    return timestampDifference || secondEvent.getId().localeCompare(firstEvent.getId());
                })[0];

            return canonicalSummary?.getId() === matrixEvent.getId();
        }

        const terminationType = getMatrixRtcTerminationType(matrixEvent);
        if (terminationType) {
            const callId = matrixEvent.getContent?.()?.call_id;
            const callEvents = getCallTimelineEvents(matrixEvent);
            const hasSummary = callEvents.some(event => getCallSummary(event)?.call_id === callId);
            if (hasSummary) {
                return false;
            }

            const canonicalTermination = callEvents
                .filter(event => (
                    getMatrixRtcTerminationType(event) &&
                    event.getContent?.()?.call_id === callId
                ))
                .sort((firstEvent, secondEvent) => {
                    const timestampDifference = firstEvent.getTs() - secondEvent.getTs();
                    return timestampDifference || firstEvent.getId().localeCompare(secondEvent.getId());
                })[0];

            return canonicalTermination?.getId() === matrixEvent.getId();
        }

        if (![CALL_HANGUP, CALL_REJECT].includes(matrixEvent.getType())) {
            return false;
        }

        if (currentChat?.isGroupChat && matrixEvent.getType() === CALL_HANGUP) {
            return false;
        }

        return true;
    };

    /**
     * Go back to the previous, or to the next event 
     * until we can get a valid message (only non-redacted messaging events)
     * and get the sender_id
     */
    const findPreviousSenderUserId = (startingIndex) => {
        if (startingIndex !== 0) {
            for (let i = startingIndex - 1; i >= 0;i--) {
                if (roomTimeline[i]?.getSender()
                    && !roomTimeline[i].isRedacted()
                    && [ROOM_ENCRYPTED, ROOM_MESSAGE].includes(roomTimeline[i].getType())
                ) {
                    return roomTimeline[i]?.getSender();
                }
            }
        }
        return null;
    };

    const findNextSenderUserId = (startingIndex) => {
        if (startingIndex !== roomTimeline.length) {
            for (let i = startingIndex + 1; i < roomTimeline.length; i++) {
                if (roomTimeline[i]?.getSender()
                    && !roomTimeline[i].isRedacted()
                    && [ROOM_ENCRYPTED, ROOM_MESSAGE].includes(roomTimeline[i].getType())
                ) {
                    return roomTimeline[i]?.getSender();
                }
            }
        }
        return null;
    };

    return {
        renderTimelineEvent,
        isAMembershipEvent,
        isAValidMessageEvent,
        displayMembershipTimelineEvent
    };
};
